import { useState } from "react";
import AuthImage from "./AuthImage";
import Lightbox from "./Lightbox";
import PostVideo from "./PostVideo";
import { ExternalLink } from "lucide-react";

/** Build a streaming src for our auth-protected file endpoint. The browser
 * can't attach an Authorization header to <video src>, so we pass the JWT
 * via ?auth= query param (the backend supports both header & query). */
function authedFileUrl(url) {
  if (!url) return "";
  // Strip /api prefix to match axios baseURL handling; AuthImage does the
  // same. We keep it here as well-formed absolute path so it works in dev
  // (proxy) and prod (same-origin /api -> backend).
  const tok = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
  const sep = url.includes("?") ? "&" : "?";
  return tok ? `${url}${sep}auth=${encodeURIComponent(tok)}` : url;
}

export function VideoEmbed({ embed }) {
  if (!embed) return null;
  let iframeSrc = null;
  if (embed.provider === "youtube") iframeSrc = `https://www.youtube.com/embed/${embed.video_id}`;
  // Vimeo needs `dnt=1` to avoid tracking-consent overlays that occasionally
  // hide the video frame on desktop Chrome even when audio plays.
  if (embed.provider === "vimeo") iframeSrc = `https://player.vimeo.com/video/${embed.video_id}?dnt=1&title=0&byline=0&portrait=0`;
  return (
    <div className="mt-3 rounded-md overflow-hidden border border-[rgba(0,170,255,0.18)] bg-black w-full max-w-full" data-testid="video-embed">
      {/* Classic responsive-iframe wrapper (padding-bottom hack). We do NOT
          use CSS `aspect-ratio` + `position:absolute` here because it hits
          a Chromium compositor bug that shows Vimeo's chrome + audio but a
          black video frame. Explicit width/height iframe attrs are
          required for reliable Vimeo playback. */}
      <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
        {iframeSrc ? (
          <iframe
            src={iframeSrc}
            title={embed.title || "Video"}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
          />
        ) : null}
      </div>
      <a
        href={embed.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono-tech uppercase tracking-[0.18em] text-[#00aaff] hover:text-[#39ff14] min-w-0"
      >
        <ExternalLink size={12} className="shrink-0" />
        <span className="shrink-0">Watch on {embed.provider === "youtube" ? "YouTube" : "Vimeo"}</span>
        {embed.title ? <span className="text-[#94a3b8] normal-case tracking-normal ml-2 truncate">{embed.title}</span> : null}
      </a>
    </div>
  );
}

export function MediaGallery({ media }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  if (!media?.length) return null;
  const images = media.filter((m) => m.kind !== "video");
  return (
    <>
      <div className={`mt-3 grid gap-2 w-full max-w-full ${media.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
        {media.map((m) =>
          m.kind === "video" ? (
            <PostVideo
              key={m.file_id}
              src={authedFileUrl(m.url)}
              contentType={m.content_type}
              fileId={m.file_id}
            />
          ) : (
            <AuthImage
              key={m.file_id}
              src={m.url}
              alt="post media"
              className="rounded-md max-h-[420px] w-full max-w-full object-cover border border-[rgba(0,170,255,0.18)] cursor-zoom-in"
              onClick={() => setLightboxIdx(images.findIndex((x) => x.file_id === m.file_id))}
              data-testid={`post-media-${m.file_id}`}
            />
          ),
        )}
      </div>
      {lightboxIdx !== null && (
        <Lightbox images={images} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </>
  );
}

export function LinkPreview({ preview }) {
  if (!preview) return null;
  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-3 flex gap-3 p-3 rounded-md border border-[rgba(0,170,255,0.18)] hover:border-[#00aaff] transition-all w-full max-w-full min-w-0"
      data-testid="link-preview"
    >
      {preview.image && (
        <img
          src={preview.image}
          alt=""
          className="w-24 h-24 object-cover rounded-md border border-[rgba(0,170,255,0.18)] flex-shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">
          {preview.site_name || new URL(preview.url).hostname}
        </div>
        <div className="text-sm text-white font-medium truncate">{preview.title}</div>
        {preview.description && (
          <div className="text-xs text-[#94a3b8] line-clamp-2 csc-break-anywhere">{preview.description}</div>
        )}
      </div>
    </a>
  );
}
